import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { ROLE_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import useGetRolesByIds from 'bundles/onboarding-common/hooks/useGetRolesByIds';
import type { RoleWithState } from 'bundles/onboarding-common/types';

type UseFetchRolesFromIdsParams = {
  roleIds: string[];
};

const useFetchRolesFromIds = ({ roleIds }: UseFetchRolesFromIdsParams) => {
  const [roles, setRoles] = useState<RoleWithState[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getRolesByIds } = useGetRolesByIds();

  useEffect(() => {
    if (roleIds.length === 0) {
      setRoles([]);
      return;
    }

    const fetchRolesFromIds = async () => {
      setIsLoading(true);
      try {
        const { data } = await getRolesByIds({ variables: { ids: roleIds } });
        const fetchedRoles =
          data?.PathwaySkillCareerRoles?.queryRolesByIds.map((role) => ({
            ...role,
            type: ROLE_OPTIONS_TYPE.DEFAULT,
            isVisible: true,
            isSelected: true,
            isSearchResult: false,
            isPartOfSearch: false,
          })) || [];
        setRoles(fetchedRoles);
      } catch (error) {
        Sentry.captureMessage('Error fetching roles from ids', {
          extra: {
            error: error.message,
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchRolesFromIds();
  }, [roleIds, getRolesByIds]);
  return {
    roles,
    isLoading,
  };
};

export default useFetchRolesFromIds;
